/**
 * AI 안전 — 위기 발화 감지와 안전 응답.
 *
 * 왜 필요한가: 우리는 "고민 한 줄"을 자유 텍스트로 받는다. 타로를 보러 온 사람이
 * 거기에 자살·자해 이야기를 적는 일은 실제로 일어난다. 그 상태에서 카드 해석을
 * 돌려주는 건(하물며 죽음·탑 카드가 나오면) 사람에게 해가 된다.
 *
 * 그래서 **해석 대신 상담 안내를 먼저 보여주고, LLM은 아예 호출하지 않는다.**
 * 이용 횟수도 차감하지 않는다 — 도움을 청한 사람에게서 횟수를 뺏는 셈이 되면 안 된다.
 *
 * 앱인토스 입점의 AI 채팅·상담 요건이기도 하다 (doc/apps-in-toss.md, doc/ai-safety.md).
 */

/** 위기 안내에서 보여줄 상담 창구. 번호를 코드 곳곳에 박지 않기 위한 단일 출처 */
export const CRISIS_HELPLINES = [
  { name: '자살예방 상담전화', tel: '109', note: '24시간 · 전화·문자 모두 가능해요' },
  { name: '정신건강 상담전화', tel: '1577-0199', note: '24시간' },
  { name: '청소년 전화', tel: '1388', note: '24시간 · 채팅 상담도 있어요' },
  { name: '긴급 신고', tel: '119', note: '지금 위험하다면 바로' },
] as const;

/** 위기 안내 문구 — 상담사 톤 유지(존댓말), 놀라게 하거나 나무라지 않는다 */
export const CRISIS_NOTICE = {
  title: '지금은 카드보다 먼저 할 이야기가 있어요',
  body: '적어주신 말이 마음에 걸려요. 이번에는 해석 대신, 지금 바로 이야기 나눌 수 있는 곳을 알려드릴게요. 혼자 견디지 않으셔도 돼요.',
  footer:
    '고민 문구를 지우시면 타로는 언제든 다시 보실 수 있어요. 이번 요청은 이용 횟수에서 빠지지 않았어요.',
} as const;

/**
 * 위기 감지로 요청을 되돌릴 때 쓰는 HTTP 상태.
 * 422(Unprocessable) — 요청 형식은 맞지만 내용상 처리하지 않는다는 뜻. 서버·웹이 같은 값을 본다.
 */
export const CRISIS_HTTP_STATUS = 422;

/**
 * 위기 신호로 보는 표현.
 *
 * 판정은 **공백을 지운 소문자 문자열**에 대해 한다 ("죽 고 싶 다" 같은 띄어쓰기 우회 방지).
 * 관용적 강조("배고파 죽겠어", "힘들어 죽겠다")는 애초에 목록에 넣지 않았다 —
 * 한국어에서 `죽겠`은 대부분 강조 어미라 넣는 순간 오탐이 쏟아진다.
 */
const CRISIS_PATTERNS: readonly RegExp[] = [
  /자살(?!골|률)/,
  /죽고싶|죽고파|죽어버리/,
  /살고싶지않|살기싫|살이유가없|살아갈이유|살아있기싫/,
  /목숨을?끊|스스로목숨/,
  /극단적인?선택/,
  /자해/,
  /손목.{0,2}(긋|그어|그은|그었)|그어버리/,
  /사라지고싶|사라져버리|없어지고싶/,
  /유서를?(쓰|써|썼|남기)/,
  /죽는방법|죽을방법|편하게죽|안아프게죽/,
  /뛰어내리고싶|뛰어내릴까/,
  /목을매|목매달|목맬/,
  /번개탄/,
  /수면제.{0,3}모(으|아)/,
  /죽이고싶|죽여버리고싶/, // 타해 발화도 같은 통로로 안내한다
  /같이죽/,
];

/**
 * 위기 신호로 보지 않는 관용구. 판정 전에 **그 표현만** 걷어낸다.
 * ("창피해서 죽고 싶다" 같은 말까지 막으면 일상적인 고민을 못 적는다)
 *
 * ⚠️ 뒤에 `[가-힣]*`를 붙이지 말 것. 판정은 **공백을 지운** 문자열에 대해 하므로
 * 문장 전체가 하나의 한글 덩어리가 되고, 그러면 관용구 뒤에 이어지는 진짜 위기 발화까지
 * 통째로 지워진다. "창피해서 죽고 싶고 자해도 했어요"가 빈 문자열이 되어
 * **감지를 빠져나갔다** (2026-08-26 코드리뷰에서 발견).
 */
const IDIOM_PATTERNS: readonly RegExp[] = [/(?:창피|부끄|쪽팔|민망)[가-힣]{0,4}죽고싶/g];

/** 판정용 정규화 — 소문자 + 공백 제거 */
function normalize(text: string): string {
  let t = text.toLowerCase().replace(/\s+/g, '');
  for (const idiom of IDIOM_PATTERNS) t = t.replace(idiom, '');
  return t;
}

/**
 * 위기 발화인가. 값이 없으면 항상 `false` (고민은 선택 입력이다).
 *
 * ⚠️ 이 함수는 **서버가 최종 판정자**다. 웹에서도 같은 함수를 써서 입력 즉시 안내를 띄우지만,
 * 그건 사용자를 빨리 돕기 위한 것이고 우회 가능하다. 해석을 실제로 막는 건 서버다.
 */
export function detectCrisis(text?: string | null): boolean {
  if (!text) return false;
  const t = normalize(text);
  if (!t) return false;
  return CRISIS_PATTERNS.some((re) => re.test(t));
}
